import {
  Button,
  Checkbox,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import PublishDialogIcon from "@entrilia/shared/icons/PublishDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { DataCollectionRequestDetails } from "../../../../api/types/dataCollectionTypes";

interface Props {
  dataRequestId: string;
  submissionIds: string[];
  recipientNames: string[];
  open: boolean;
  onClose: () => void;
  onApply: (dataRequest: DataCollectionRequestDetails) => void;
}

const publishDataRequestSubmissions = withErrorHandling(adminApi.publishDataRequestSubmissions);

const PublishSubmissionsDialog = ({ dataRequestId, submissionIds, recipientNames, open, onClose, onApply }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [isSaving, setIsSaving] = useState(false);
  const [sendEmailNotifications, setSendEmailNotifications] = useState(true);
  const [userComment, setUserComment] = useState("");

  const handleApply = async () => {
    setIsSaving(true);
    const [dataRequest, error] = await publishDataRequestSubmissions(dataRequestId, {
      submissionIds,
      sendEmailNotifications,
      commentUserInput: sendEmailNotifications && userComment.trim() ? userComment.trim() : undefined,
    });
    setIsSaving(false);

    if (error) {
      logError(error, "[PublishSubmissionsDialog] publishDataRequestSubmissions");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Published successfully");
    onApply(dataRequest);
  };

  const prompt =
    recipientNames.length > 1
      ? `Publish ${recipientNames.length} submissions`
      : `Publish submission for ${recipientNames[0]}`;

  const description = `The ${submissionIds.length > 1 ? "submissions" : "submission"} will be available for users to view and update.`;

  return (
    <SmallDialog open={open} onClose={onClose}>
      <DialogTitle>Publish</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <PublishDialogIcon sx={{ height: 80, width: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6" sx={{ textAlign: "center" }}>
              {prompt}
            </Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              {description}
            </Typography>
            <FormControlLabel
              label="Send notification emails"
              control={
                <Checkbox
                  checked={sendEmailNotifications}
                  onChange={(_, checked) => setSendEmailNotifications(checked)}
                />
              }
            />
            {sendEmailNotifications && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Include message (optional)"
                value={userComment}
                onChange={(e) => setUserComment(e.target.value)}
              />
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" loading={isSaving} onClick={handleApply}>
          Publish
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default PublishSubmissionsDialog;
